import { darkBackgroundBlur } from '@repo/ui/components/utilities';
import { getIsNewThemeActive, themeStyle } from '@repo/ui/theming';
import { layeringCSSVariables } from '@repo/ui-kit/css/utils';
import styled from 'styled-components';

import { PopoverPlacement, PopoverProps, PopoverTheme } from './types';

const PONTER_LINE_TIP_SIZE = 4;
const VISIBLE_TIP_HEIGHT = 5;

const isNewThemeActive = getIsNewThemeActive();

const getWrapperTransform = (
  placement: PopoverPlacement,
  showPointerLine: boolean
) => {
  switch (placement) {
    case PopoverPlacement.Top:
      return showPointerLine
        ? `margin-bottom: -${PONTER_LINE_TIP_SIZE}px;`
        : `transform: translateY(-${VISIBLE_TIP_HEIGHT}px)`;
    case PopoverPlacement.Right:
      return showPointerLine
        ? `margin-right: -${PONTER_LINE_TIP_SIZE}px;`
        : `transform: translateX(${VISIBLE_TIP_HEIGHT}px)`;
    case PopoverPlacement.Left:
      return showPointerLine
        ? `margin-right: -${PONTER_LINE_TIP_SIZE}px;`
        : `transform: translateX(-${VISIBLE_TIP_HEIGHT}px)`;
    case PopoverPlacement.Bottom:
      return showPointerLine
        ? `margin-top: -${PONTER_LINE_TIP_SIZE}px;`
        : `transform: translateY(${VISIBLE_TIP_HEIGHT}px)`;
    default:
      return '';
  }
};

export const PopoverWrapper = styled.div<{
  width: string;
  height?: string;
  isInPosition: boolean;
  margin: string;
  placement: Exclude<PopoverProps['placement'], undefined>;
  showPointerLine?: boolean;
}>`
  position: absolute;

  width: ${(props) => props.width};
  padding: ${(props) => props.margin};
  box-sizing: border-box;
  opacity: ${(props) => (props.isInPosition ? '1' : '0')};
  transition: opacity 0.5s;
  z-index: ${(props) => props.theme.zIndex};
  margin: ${(props) => props.theme.margin};
  cursor: ${(props) => props.theme.cursor ?? 'default'};
  ${({ placement, showPointerLine = false }) =>
    getWrapperTransform(placement, showPointerLine)}
`;

export const Content = styled.div<{ theme: PopoverTheme }>`
  position: relative;
  max-height: 100vh;
  width: 100%;
  overflow-y: ${({ theme }) => theme?.contentOverflowY ?? 'auto'};
  box-sizing: border-box;
  background-color: ${(props) => props.theme.backgroundColor};
  box-shadow: ${(props) => props.theme.boxShadow};
  border-radius: ${(props) => props.theme.borderRadius};
  padding: ${({ theme }) => (theme.padding ? `${theme.padding}` : '10px')};
  border-bottom-left-radius: ${(props) => props.theme.borderBottomLeftRadius};
  ${darkBackgroundBlur}
`;

export const PopoverTip = styled.div<{
  margin: string;
  placement: Exclude<PopoverProps['placement'], undefined>;
}>`
  position: absolute;
  margin: ${(props) => props.margin};
  height: 10px;
  width: 10px;
  z-index: ${(props) => props.theme.zIndexTip};
  border-radius: 0px 1px 0px 0px;
  background-color: ${(props) => props.theme.backgroundColor};
  ${({ placement }) => {
    const values = {
      top: 'bottom: -4px; transform: rotate(-225deg);',
      bottom: 'top: -4px; transform: rotate(-45deg);',
      left: 'right: -4px; transform: rotate(45deg);',
      right: 'left: -4px; transform: rotate(225deg);',
    };

    return values[placement];
  }}
`;

const getPointerLinePlacement = (
  placement: NonNullable<PopoverProps['placement']>,
  height: number
) => {
  const halfHeight = height / 2;
  const styles = {
    right: `
      top: 50%;
      left: -${halfHeight}px;
      transform: translate(-50%, -50%) rotate(-90deg);
    `,
    left: `
      top: 50%;
      right: -${halfHeight}px;
      transform: translate(50%, -50%) rotate(90deg);
    `,
    top: `
      left: 50%;
      bottom: -${halfHeight}px;
      transform: translate(-50%, 50%) rotate(180deg);
    `,
    bottom: `
      left: 50%;
      top: -${halfHeight}px;
      transform: translate(-50%, -50%);
    `,
  };

  return styles[placement];
};

const getPointerLineTip = (backgroundColor: string) => `
  content: '';
  position: absolute;
  width: ${PONTER_LINE_TIP_SIZE}px;
  height: ${PONTER_LINE_TIP_SIZE}px;
  background-color: ${backgroundColor};
  border-radius: 50%;
  top: -3px;
  left: -1px;
`;

export const PointerLine = styled.div<{
  placement: NonNullable<PopoverProps['placement']>;
  height: number;
}>`
  position: absolute;
  width: 2px;
  height: ${({ height }) => `${height}px`};
  background-color: ${(props) => props.theme.backgroundColor};
  z-index: ${(props) => props.theme.zIndexTip};

  ${({ placement, height }) => getPointerLinePlacement(placement, height)}

  &:before {
    ${({ theme }) => getPointerLineTip(theme.backgroundColor)}
  }
`;

export const PopoverContainer = styled.div`
  position: absolute;
  top: 0px;
  left: 0px;
  width: 100%;
  pointer-events: auto;
  z-index: ${(props) =>
    props.theme.zIndex ?? layeringCSSVariables.zIndexOverlay};
`;

export const HeadWrapper = styled.div<{ theme: PopoverTheme }>`
  padding: ${(props) => props.theme.headPadding};
  border-bottom: ${({ theme }) => (theme.hasHeadBorder ? '1px solid' : 'none')};
  border-color: ${isNewThemeActive
    ? themeStyle.varInkLight
    : themeStyle.varInkSuperLight};
  box-sizing: border-box;
`;
